import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { EntityType } from '../../domain/entityTypes.js';
import { ENTITY_CALLS } from '../../hooks/entityCalls.js';
import { useTRPC } from '../../utils/trpc.js';

/**
 * Everything the drawer needs from the server. Read-only: editing master is a page, never a
 * textarea in a drawer. The query comes from the type's `ENTITY_CALLS` entry, so a new type
 * needs no branch here.
 *
 * `focus` starts at `focusEnvironment` and never chases it again — `EntityDrawer` keys its host
 * component on `type`/`entityKey`/`focusEnvironment`, so a new entity or a different cell clicked
 * remounts this hook with a fresh initial value instead of a `useEffect` one render behind.
 */
export const useEntityDetail = (type: EntityType, entityKey: string, focusEnvironment?: string) => {
    const trpc = useTRPC();
    const [focus, setFocus] = useState<string | undefined>(focusEnvironment);

    const query = useQuery(ENTITY_CALLS[type].detailQuery(trpc, entityKey, focus));

    return {
        isLoading: query.isLoading,
        detail: query.data?.detail,
        /** The record's name, when master or an environment reports one. */
        title: query.data?.detail?.title ?? null,
        /** The record's grid row (roll-ups, child count) when its type has one and it is in master. */
        row: query.data?.row ?? null,
        focus,
        setFocus,
        focused: query.data?.detail?.environments.find((e) => e.name === focus),
    };
};

export type EntityDetailState = ReturnType<typeof useEntityDetail>;
